import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { GlossaryTerms } from "@/components/calv/GlossaryTerms";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/seo-ai-seo")({
  head: () => {
    const h = pageHead("seoAiSeo", "en");
    return { meta: h.meta, links: h.links };
  },
  component: SeoPage,
});

const steps = [
  { num: "01", title: "Analysis of visibility, demand, and economic impact", body: "We don't just analyze rankings, but primarily their actual contribution to the business. Which search queries are purchase-ready, which pages contribute to revenue, where organic traffic converts. The goal is to identify SEO potentials with real economic leverage." },
  { num: "02", title: "Economic prioritization instead of SEO activism", body: "Not every SEO measure is worthwhile – and many are economically irrelevant. We prioritize by expected impact on revenue, search intent, feasibility, and scalability. This creates an SEO roadmap that's not theoretically correct, but economically sensible." },
  { num: "03", title: "Cleanly integrating structure, content & search intent", body: "SEO works best when structure and content precisely match the user's search and decision situation. We optimize page and category structures, content structures, and internal linking. It's not about more content, but the right content in the right place." },
  { num: "04", title: "SEO, PPC, and conversion as a connected system", body: "SEO realizes its full value in interaction with other channels. PPC shows early which search terms convert. SEO takes over these topics sustainably. SEO thus reduces long-term dependence on rising click costs and stabilizes the overall system." },
];

const capabilities = [
  { name: "Technical SEO", detail: "Crawlability, indexing, Core Web Vitals" },
  { name: "Content structures", detail: "Argumentation logic, search intent" },
  { name: "Page & category structure", detail: "Hierarchies, internal linking" },
  { name: "Service & product pages", detail: "Conversion-oriented" },
  { name: "AI SEO", detail: "AI-driven analysis, topic spaces" },
  { name: "Competitive analysis", detail: "Market structure, gap analysis" },
  { name: "Prioritization", detail: "Economic leverage over rankings" },
  { name: "Integration with PPC & CRO", detail: "As a connected system" },
];

const stack = [
  { name: "Google Search Console", small: "Performance, Indexing" },
  { name: "Google Analytics (GA4)", small: "Behavior, conversions" },
  { name: "Screaming Frog", small: "Technical SEO audits" },
  { name: "Ahrefs", small: "Backlinks, keywords" },
  { name: "Sistrix", small: "Visibility, competition" },
  { name: "Matomo", small: "Privacy-compliant analytics" },
  { name: "Seobility", small: "On-page analysis" },
  { name: "AI analysis workflows", small: "Topic-space structuring" },
];

function SeoPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="SEO & AI SEO"
          headline={<>SEO isn't <em>a reach metric,</em> it's an economic lever.</>}
          lead="Classical SEO is only half the road. We optimize in parallel for Google, ChatGPT, Perplexity and Claude — because search increasingly happens inside AI, not in result lists."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "SEO & AI SEO" }]}
        />

        <Section number="02" kicker="Standpoint" headline={<>Visibility is only valuable when it has <em>economic impact.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Many companies invest for years in SEO and achieve visibility – without it noticeably reflecting in business results. The reason is rarely lack of work. The reason is almost always wrong prioritization.</p>
              <p className="mb-5">For us, SEO doesn't mean occupying as many keywords as possible. It means identifying search queries with real business relevance and systematically addressing them to generate measurable results. Visibility is only valuable when it has economic impact.</p>
              <p className="mb-5">Our SEO approach is not isolated, but always part of an overall system of demand, conversion, and profitability. We view SEO not from a ranking perspective, but from a business perspective.</p>
              <p>Where clients have their own content capacity, we advise on content strategy and deliver briefings, topic plans, and optimization recommendations — the internal teams then write the texts. Where this capacity is lacking, we create content ourselves, based on intensive keyword research, search-intent analysis, and competitive gap analysis. The range extends from long-form guides and pillar pages through conversion-oriented service and category texts to AI-driven content pipelines for programmatic SEO, where volume is strategically necessary. In all cases: content is not an end in itself — texts that rank but aren't embedded in conversion paths are burned effort. We always interlink content production with the performance logic of the entire setup.</p>
            </div>
          </RevealOnScroll>
          <Callout>Not all visibility is valuable. We only invest where SEO measurably contributes to the business.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Approach" headline={<>Four phases that make SEO <em>economically sensible.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-12 rounded-lg bg-white p-6 md:p-10">
              <div className="grid gap-8 md:grid-cols-[minmax(0,_2fr)_minmax(0,_3fr)] md:gap-14">
                <div>
                  <p className="eyebrow mb-3">Optional building block</p>
                  <h3 className="font-display text-xl text-ink">Content creation in the mandate mix</h3>
                </div>
                <p className="text-[17px] leading-relaxed text-ink-2">
                  Where clients call on this capacity from us, we integrate content production into Phases 02 to 04. Concretely, that means: keyword clusters and content briefings emerge in Phase 02 (Setup), first content pieces go live from Phase 03 (Scaling), and in Phase 04 (Operations) a continuous content iteration cadence runs. Volume needs from 20–30 pieces per month we solve with AI-supported content pipelines plus human editing and brand-tonality discipline — not with pure AI outputs without editorial hand.
                </p>
              </div>
            </div>
          </RevealOnScroll>
          <Callout>SEO is not an isolated channel. It's a stability factor in the entire performance system.</Callout>
        </Section>

        <Section number="04" kicker="Capabilities & stack" headline={<>What we use to make SEO <em>steerable.</em></>}>
          <Capabilities items={capabilities} />
          <RevealOnScroll>
            <p className="mt-8 max-w-2xl text-[15px] leading-relaxed text-ink-3">
              We run AI-SEO as its own discipline —{" "}
              <Link
                to="/en/services/geo-generative-engine-optimization"
                className="text-cyan-deep underline-offset-4 hover:underline"
              >
                Generative Engine Optimization (GEO)
              </Link>
              . It builds on the same technical foundation and decides whether AI systems name you as a source.
            </p>
          </RevealOnScroll>
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="en" number="05" />

        <RelatedServices
          locale="en"
          fromId="seo-ai-seo"
          number="06"
          items={relatedBySource["seo-ai-seo"]}
        />

        <Section number="07" kicker="Engagement">
          <CTABlock
            headline={<>Is your SEO <em>economically prioritized?</em></>}
            body="Want to know which organic topics really benefit your business – and which don't?"
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
        <GlossaryTerms locale="en" ids={["ai-overviews", "ai-mode", "geo", "pillar-content", "snippet-suitability", "eeat"]} />
      </main>
      <Footer />
    </>
  );
}
